/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useState} from 'react'
import clsx from 'clsx'
import {MagnifyingGlassIcon, XMarkIcon} from '@heroicons/react/24/solid'
import {useDebouncedCallback} from 'use-debounce'
import {useTranslation} from 'react-i18next'

interface UiTableColumnSearchProps {
    value: string | number
    onChange: (value: string | number) => void
    placeholder?: string
    debounceTime?: number
    autoFocus?: boolean
    hideSearchIcon?: boolean
}

const UiTableColumnSearch: React.FC<UiTableColumnSearchProps> = ({
    value,
    onChange,
    placeholder,
    debounceTime = 300,
    autoFocus = false,
    hideSearchIcon = false,
}) => {
    const { t } = useTranslation()
    const finalPlaceholder = placeholder || t('Search')
    const [internalValue, setInternalValue] = useState(value ?? '')

    const debouncedChangeHandler = useDebouncedCallback(
        (data) => onChange(data),
        debounceTime
    )

    const onChangeHandler = (newValue: string) => {
        setInternalValue(newValue)
        debouncedChangeHandler(newValue)
    }

    const clearHandler = () => {
        setInternalValue('')
        onChange('')
    }

    useEffect(() => {
        setInternalValue(value ?? '')
    }, [value])

    return (
        <div
            className={clsx(
                'UiTableColumnSearch flex flex-row items-center justify-start w-full relative bg-white/50 border border-gray-300/80 rounded-md transition-all duration-150 focus-within:ring-1 focus-within:ring-blue-400 focus-within:bg-white dark:bg-gray-900/50 dark:border-gray-700/80 dark:focus-within:bg-gray-900 font-normal mt-1.5',
                // min-w-[100px] allows it to shrink below default input sizes if column is narrow
                'min-w-0'
            )}
            onClick={(e) => e.stopPropagation()}
        >
			{!internalValue && !hideSearchIcon && (
            <div className="absolute left-1.5 flex items-center pointer-events-none">
                <MagnifyingGlassIcon
                    className={clsx(
                        'w-3.5 h-3.5',
                        internalValue
                            ? 'text-blue-500'
                            : 'text-gray-400 dark:text-gray-500'
                    )}
                />
            </div>
				)}
            <input
                className={clsx(
                    'w-full inline-flex items-center  py-[5px] text-xs placeholder:text-xxs font-medium transition duration-150 bg-transparent placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-0 focus:border-0 border-0',
                    internalValue
                        ? 'text-blue-500 font-semibold pl-1 pr-1'
                        : hideSearchIcon
                          ? 'text-gray-700 dark:text-gray-100 pl-2 pr-1'
                          : 'text-gray-700 dark:text-gray-100 pl-6 pr-1'
                )}
                placeholder={finalPlaceholder}
                type="text"
                autoFocus={autoFocus}
                size={1}
                value={internalValue}
                onChange={(e) => onChangeHandler(e.target.value)}
            />
            {internalValue && (
                <button
                    className={clsx(
                        'absolute right-1.5 flex items-center justify-center w-4 h-4 rounded transition-all duration-150',
                        'text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 dark:hover:text-gray-300'
                    )}
                    onClick={clearHandler}
                    type="button"
                >
                    <XMarkIcon className="w-3 h-3" />
                </button>
            )}
        </div>
    )
}

export default UiTableColumnSearch
